import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Typography, Grid, Card, CardContent, Alert, CircularProgress, Link, Divider, Chip, useTheme, Skeleton } from "@mui/material";
import {
  Notifications as NoticeIcon,
  Warning as BreachIcon,
  DeleteForever as ErasureIcon,
  Info as InfoIcon,
  Description as ConsentIcon,
  TrackChanges as PurposeIcon,
  Storage as DataIcon,
  Assignment as RequestIcon,
} from "@mui/icons-material";
import DashboardSummaryCard from "../components/DashboardSummaryCard";
import DashboardActionItems from "../components/DashboardActionItems";
import DashboardRecentActivity from "../components/DashboardRecentActivity";
import DashboardSection from "../components/DashboardSection";
import { noticesService, NoticesSummary } from "../services/notices-service";
import { purposeService } from "../services/purpose-service";
import { requestsService, RequestsSummary } from "../services/requests-service";
import { dataService } from "../services/data-service";
import { formsService } from "../services/forms-service";
import { ROUTES } from "../config/routes";

interface DashboardPageProps {
  jwt: string | null;
  clientData: {
    subdomain: string;
    clientName: string;
  } | null;
  errorDetails?: {
    errorType: string;
    message: string;
  };
}

const DashboardPage: React.FC<DashboardPageProps> = ({ jwt, clientData, errorDetails }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const [displayClientData, setDisplayClientData] = useState(clientData);

  // Summary data
  const [noticesSummary, setNoticesSummary] = useState<NoticesSummary | null>(null);
  const [purposesCount, setPurposesCount] = useState<number | null>(null);
  const [dataElementsCount, setDataElementsCount] = useState<number | null>(null);
  const [requestsSummary, setRequestsSummary] = useState<{ total: number; open: number; closed: number; urgent: number } | null>(null);

  // Action items data
  const [unacknowledgedNotices, setUnacknowledgedNotices] = useState<any[]>([]);
  const [pendingWithdrawals, setPendingWithdrawals] = useState<any[]>([]);
  const [urgentRequests, setUrgentRequests] = useState<any[]>([]);

  // Recent activity data
  const [recentConsents, setRecentConsents] = useState<any[]>([]);
  const [recentNotices, setRecentNotices] = useState<any[]>([]);
  const [recentRequests, setRecentRequests] = useState<any[]>([]);

  // Loading states for each section (lazy loading)
  const [loadingNotices, setLoadingNotices] = useState(true);
  const [loadingPurposes, setLoadingPurposes] = useState(true);
  const [loadingDataElements, setLoadingDataElements] = useState(true);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [loadingActionItems, setLoadingActionItems] = useState(true);
  const [loadingRecentActivity, setLoadingRecentActivity] = useState(true);

  // Error states
  const [error, setError] = useState<string | null>(null);

  // Handle navigation state from AuthenticatePage
  useEffect(() => {
    if (location.state?.clientData) {
      setDisplayClientData(location.state.clientData);
    }
  }, [location.state]);

  // Fetch dashboard data with lazy loading - each section loads independently
  useEffect(() => {
    // Fetch notices summary
    const fetchNoticesSummary = async () => {
      setLoadingNotices(true);
      try {
        const summary = await noticesService.getNoticesSummary();
        setNoticesSummary(summary);
      } catch (err: any) {
        console.error("Error fetching notices summary:", err);
      } finally {
        setLoadingNotices(false);
      }
    };

    // Fetch purposes count
    const fetchPurposes = async () => {
      setLoadingPurposes(true);
      try {
        const purposes = await purposeService.getPurposes(false);
        setPurposesCount(purposes.length);
      } catch (err: any) {
        console.error("Error fetching purposes:", err);
      } finally {
        setLoadingPurposes(false);
      }
    };

    // Fetch data elements count
    const fetchDataElements = async () => {
      setLoadingDataElements(true);
      try {
        const dataElements = await dataService.getDataElementsSummary();
        setDataElementsCount(dataElements.length);
      } catch (err: any) {
        console.error("Error fetching data elements:", err);
      } finally {
        setLoadingDataElements(false);
      }
    };

    // Fetch requests summary
    const fetchRequestsSummary = async () => {
      setLoadingRequests(true);
      try {
        const summary = await requestsService.getRequestsSummary();
        setRequestsSummary(summary);
      } catch (err: any) {
        console.error("Error fetching requests summary:", err);
      } finally {
        setLoadingRequests(false);
      }
    };

    // Fetch action items data (notices and requests)
    const fetchActionItems = async () => {
      setLoadingActionItems(true);
      try {
        const [noticesList, requestsList, purposes] = await Promise.allSettled([
          noticesService.getNotices({}),
          requestsService.listRequests({ page: 1, pageSize: 10 }),
          purposeService.getPurposes(false),
        ]);

        // Process notices list for unacknowledged notices
        if (noticesList.status === "fulfilled") {
          const notices = noticesList.value || [];
          const unacknowledged = notices.filter((n: any) => !n.IsAcknowledged && n.sourceType !== "CONSENT_NOTICE");
          setUnacknowledgedNotices(
            unacknowledged.map((n: any) => ({
              assignmentId: n.AssignmentId,
              title: n.Title || n.Name || "Untitled Notice",
              noticeType: n.NoticeType || "CONSENT_NOTICE",
              assignedDate: n.EffectiveDate || n.CreatedDate,
            }))
          );
        }

        // Process requests for urgent requests
        if (requestsList.status === "fulfilled") {
          const requests = requestsList.value.requests || [];
          const urgent = requests.filter((r: any) => r.status === "PENDING" || r.status === "IN_PROGRESS");
          setUrgentRequests(urgent);
        }

        // Process purposes for pending withdrawals
        if (purposes.status === "fulfilled") {
          setPendingWithdrawals([]);
        }
      } catch (err: any) {
        console.error("Error fetching action items:", err);
      } finally {
        setLoadingActionItems(false);
      }
    };

    // Fetch recent activity data
    const fetchRecentActivity = async () => {
      setLoadingRecentActivity(true);
      try {
        const [noticesList, forms, requestsList] = await Promise.allSettled([
          noticesService.getNotices({}),
          formsService.getForms(),
          requestsService.listRequests({ page: 1, pageSize: 10 }),
        ]);

        // Process notices for recent activity
        if (noticesList.status === "fulfilled") {
          const notices = noticesList.value || [];
          setRecentNotices(
            notices.slice(0, 7).map((n: any) => ({
              assignmentId: n.AssignmentId,
              title: n.Title || n.Name || "Untitled Notice",
              noticeType: n.NoticeType || "CONSENT_NOTICE",
              assignedDate: n.EffectiveDate || n.CreatedDate,
            }))
          );
        }

        // Process forms for recent consents
        if (forms.status === "fulfilled") {
          const recentForms = forms.value
            .sort((a, b) => new Date(b.consentTimestamp).getTime() - new Date(a.consentTimestamp).getTime())
            .slice(0, 7)
            .map((form) => ({
              consentHeaderId: form.consentHeaderId,
              collectionPointName: form.collectionPointName,
              consentDate: form.consentTimestamp,
            }));
          setRecentConsents(recentForms);
        }

        // Process requests for recent activity
        if (requestsList.status === "fulfilled") {
          const requests = requestsList.value.requests || [];
          setRecentRequests(requests.slice(0, 7));
        }
      } catch (err: any) {
        console.error("Error fetching recent activity:", err);
      } finally {
        setLoadingRecentActivity(false);
      }
    };

    // Start all fetches in parallel - each will update independently
    fetchNoticesSummary();
    fetchPurposes();
    fetchDataElements();
    fetchRequestsSummary();
    fetchActionItems();
    fetchRecentActivity();
  }, []);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
          Dashboard
        </Typography>
        {displayClientData && (
          <Typography variant="body2" color="text.secondary">
            Welcome to {displayClientData.clientName}
          </Typography>
        )}
      </Box>

      {/* Error Details */}
      {errorDetails && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Access Error
          </Typography>
          <Typography variant="body2">
            <strong>Error Type:</strong> {errorDetails.errorType}
          </Typography>
          <Typography variant="body2">
            <strong>Message:</strong> {errorDetails.message}
          </Typography>
        </Alert>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Dashboard Content - Always show with lazy loading */}
      <>
        {/* Overview Section - Organized by Category */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Overview
          </Typography>
          <Grid container spacing={3}>
            {/* Notices Section */}
            <Grid item xs={12} md={6}>
              <DashboardSection title="Notices" icon={<NoticeIcon />} color={theme.palette.primary.main} linkTo={ROUTES.NOTICES}>
                {loadingNotices ? (
                  <Box>
                    <Skeleton variant="text" width="40%" height={40} sx={{ mb: 3 }} />
                    <Skeleton variant="text" width="100%" height={30} sx={{ mb: 1.5 }} />
                    <Skeleton variant="text" width="100%" height={30} sx={{ mb: 1.5 }} />
                    <Skeleton variant="text" width="100%" height={30} sx={{ mb: 1.5 }} />
                    <Skeleton variant="text" width="100%" height={30} />
                  </Box>
                ) : (
                  <Box>
                    {/* Total Notices - Simple and Clean */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h2" sx={{ fontWeight: 700, color: "text.primary", mb: 0.5 }}>
                        {noticesSummary?.total ?? 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Notices
                      </Typography>
                    </Box>

                    {/* Notice Types - Clean List */}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                      {/* Consent Notices */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          cursor: "pointer",
                          p: 1,
                          borderRadius: 1,
                          "&:hover": {
                            bgcolor: "action.hover",
                          },
                        }}
                        onClick={() => navigate(`${ROUTES.NOTICES}?noticeType=CONSENT_NOTICE`)}
                      >
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <ConsentIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            Consent
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="baseline" gap={1}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {noticesSummary?.byType.consent ?? 0}
                          </Typography>
                          {(noticesSummary?.openByType?.consent ?? 0) > 0 && (
                            <Typography
                              variant="caption"
                              sx={{
                                px: 1,
                                py: 0.25,
                                borderRadius: 1,
                                bgcolor: theme.palette.warning.light,
                                color: theme.palette.warning.dark,
                                fontWeight: 600,
                              }}
                            >
                              {noticesSummary?.openByType?.consent} need consent
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {/* Informational Notices */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          cursor: "pointer",
                          p: 1,
                          borderRadius: 1,
                          "&:hover": {
                            bgcolor: "action.hover",
                          },
                        }}
                        onClick={() => navigate(`${ROUTES.NOTICES}?noticeType=INFORMATIONAL_NOTICE`)}
                      >
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <InfoIcon fontSize="small" sx={{ color: theme.palette.info.main }} />
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            Informational
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="baseline" gap={1}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {noticesSummary?.byType.informational ?? 0}
                          </Typography>
                          {(noticesSummary?.openByType?.informational ?? 0) > 0 && (
                            <Typography
                              variant="caption"
                              sx={{
                                px: 1,
                                py: 0.25,
                                borderRadius: 1,
                                bgcolor: theme.palette.error.light,
                                color: theme.palette.error.dark,
                                fontWeight: 600,
                              }}
                            >
                              {noticesSummary?.openByType?.informational} need acknowledgment
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {/* Breach Notices */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          cursor: "pointer",
                          p: 1,
                          borderRadius: 1,
                          "&:hover": {
                            bgcolor: "action.hover",
                          },
                        }}
                        onClick={() => navigate(`${ROUTES.NOTICES}?noticeType=BREACH_NOTICE`)}
                      >
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <BreachIcon fontSize="small" sx={{ color: theme.palette.error.main }} />
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            Breach
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="baseline" gap={1}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {noticesSummary?.byType.breach ?? 0}
                          </Typography>
                          {(noticesSummary?.openByType?.breach ?? 0) > 0 && (
                            <Typography
                              variant="caption"
                              sx={{
                                px: 1,
                                py: 0.25,
                                borderRadius: 1,
                                bgcolor: theme.palette.error.light,
                                color: theme.palette.error.dark,
                                fontWeight: 600,
                              }}
                            >
                              {noticesSummary?.openByType?.breach} need acknowledgment
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {/* Erasure Notices */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          cursor: "pointer",
                          p: 1,
                          borderRadius: 1,
                          "&:hover": {
                            bgcolor: "action.hover",
                          },
                        }}
                        onClick={() => navigate(`${ROUTES.NOTICES}?noticeType=ERASURE_NOTICE`)}
                      >
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <ErasureIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            Erasure
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="baseline" gap={1}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {noticesSummary?.byType.erasure ?? 0}
                          </Typography>
                          {(noticesSummary?.openByType?.erasure ?? 0) > 0 && (
                            <Typography
                              variant="caption"
                              sx={{
                                px: 1,
                                py: 0.25,
                                borderRadius: 1,
                                bgcolor: theme.palette.error.light,
                                color: theme.palette.error.dark,
                                fontWeight: 600,
                              }}
                            >
                              {noticesSummary?.openByType?.erasure} need acknowledgment
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                )}
              </DashboardSection>
            </Grid>

            {/* Purposes Section */}
            <Grid item xs={12} md={6}>
              <DashboardSection title="Purposes" icon={<PurposeIcon />} color={theme.palette.success.main} linkTo={ROUTES.PURPOSES}>
                <Box
                  sx={{
                    textAlign: "center",
                    p: 3,
                    borderRadius: 1,
                    bgcolor: "action.hover",
                  }}
                >
                  <Typography variant="h3" sx={{ fontWeight: 700, color: "success.main", mb: 1 }}>
                    {purposesCount ?? 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Purposes
                  </Typography>
                </Box>
              </DashboardSection>
            </Grid>

            {/* Data Elements Section */}
            <Grid item xs={12} md={6}>
              <DashboardSection title="Data Elements" icon={<DataIcon />} color={theme.palette.secondary.main} linkTo={ROUTES.DATA}>
                <Box
                  sx={{
                    textAlign: "center",
                    p: 3,
                    borderRadius: 1,
                    bgcolor: "action.hover",
                  }}
                >
                  <Typography variant="h3" sx={{ fontWeight: 700, color: "secondary.main", mb: 1 }}>
                    {dataElementsCount ?? 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Data Elements Collected
                  </Typography>
                </Box>
              </DashboardSection>
            </Grid>

            {/* Rights Requests Section */}
            <Grid item xs={12} md={6}>
              <DashboardSection
                title="Rights Requests"
                icon={<RequestIcon />}
                color={theme.palette.info.main}
                linkTo={ROUTES.REQUESTS}
                actionItems={[
                  {
                    label: "Open",
                    count: requestsSummary?.open ?? 0,
                    color: (requestsSummary?.open ?? 0) > 0 ? "warning" : "default",
                    onClick: () => navigate(ROUTES.REQUESTS),
                  },
                ]}
              >
                <Box
                  sx={{
                    textAlign: "center",
                    p: 3,
                    borderRadius: 1,
                    bgcolor: "action.hover",
                  }}
                >
                  <Typography variant="h3" sx={{ fontWeight: 700, color: "info.main", mb: 1 }}>
                    {requestsSummary?.total ?? 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Requests
                  </Typography>
                </Box>
              </DashboardSection>
            </Grid>
          </Grid>
        </Box>

        {/* Main Content Grid */}
        <Grid container spacing={3}>
          {/* Action Items */}
          <Grid item xs={12} md={6}>
            <DashboardActionItems
              unacknowledgedNotices={unacknowledgedNotices}
              pendingWithdrawals={pendingWithdrawals}
              urgentRequests={urgentRequests}
              loading={loadingActionItems}
            />
          </Grid>

          {/* Recent Activity */}
          <Grid item xs={12} md={6}>
            <DashboardRecentActivity
              recentConsents={recentConsents}
              recentNotices={recentNotices}
              recentRequests={recentRequests}
              loading={loadingRecentActivity}
            />
          </Grid>
        </Grid>

        {/* Quick Links */}
        <Box sx={{ mt: 4 }}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Quick Links
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={2}>
                <Link href={ROUTES.NOTICES} underline="hover" color="primary">
                  View All Notices
                </Link>
                <Divider orientation="vertical" flexItem />
                <Link href={ROUTES.PURPOSES} underline="hover" color="primary">
                  Manage Purposes
                </Link>
                <Divider orientation="vertical" flexItem />
                <Link href={ROUTES.DATA} underline="hover" color="primary">
                  View My Data
                </Link>
                <Divider orientation="vertical" flexItem />
                <Link href={ROUTES.REQUESTS} underline="hover" color="primary">
                  Rights Requests
                </Link>
                <Divider orientation="vertical" flexItem />
                <Link href={ROUTES.SUPPORT} underline="hover" color="primary">
                  Contact Support
                </Link>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </>
    </Box>
  );
};

export default DashboardPage;
